import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface Category {
  Id: string;
  Name: string;
  Description: string;
  HexColor: string;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  private api: AxiosInstance;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private authService: KeycloakAuthService, private apiConfig: ApiConfigService) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  public resetCache(): void {
    this.cache.clear();
  }

  private isCacheValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < this.CACHE_DURATION;
  }

  private getCachedData<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (entry && this.isCacheValid(entry)) {
      return entry.data;
    }
    if (entry) {
      this.cache.delete(key); // Remove expired entry
    }
    return null;
  }

  private setCacheData<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  async getCategories(): Promise<Array<Category> | null> {
    const cacheKey = 'categories';

    // Check cache first
    const cachedData = this.getCachedData<Array<Category>>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Check if there's already a pending request
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Create new request
    const requestPromise = this.fetchCategories();
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      if (result) {
        this.setCacheData(cacheKey, result);
      }
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async fetchCategories(): Promise<Array<Category> | null> {
    try {
      const response = await this.api.get("/category");
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getCategory(id: string): Promise<Category | null> {
    const cacheKey = `category-${id}`;

    // Check cache first
    const cachedData = this.getCachedData<Category>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await this.api.get("/category/" + id);
      const data = response.data;
      this.setCacheData(cacheKey, data);
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateCategory(category: Category): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put("/category/" + category.Id, category);
      // Clear cache after update
      this.cache.clear();
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createCategory(category: Category): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.post("/category", category);
      // Clear cache after create
      this.cache.clear();
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete("/category/" + id);
      // Clear cache after delete
      this.cache.clear();
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
